import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../utils/api';
import { setUnauthorizedHandler } from '../utils/api';
import API_CONFIG from '../config/api';
import { removeAuthToken, initializeAuthToken } from '../utils/tokenManager';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initEffectIdRef = useRef(0);

  // Initialize token on mount
  useEffect(() => {
    initializeAuthToken();
  }, []);

  // Register global 401 handler so expired sessions redirect to login
  useEffect(() => {
    setUnauthorizedHandler(() => {
      setUser(null);
      removeAuthToken();
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  const fetchUserInfo = useCallback(async () => {
    try {
      const response = await apiClient.get('/api/auth/me');
      // React will automatically ignore state updates if component is unmounted
      setUser(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching user info:', error);
      // If token is invalid, clear user state
      if (error.response?.status === 401) {
        setUser(null);
        removeAuthToken();
      }
      throw error;
    }
  }, []);

  // Initialize auth state on mount
  useEffect(() => {
    // Increment effect ID for this effect instance
    const effectId = ++initEffectIdRef.current;

    const initializeAuthState = async () => {
      try {
        await fetchUserInfo();
      } catch (error) {
        // Silently handle 401 errors during initialization (user not logged in)
        // Other errors are already logged in fetchUserInfo
        if (error.response?.status !== 401) {
          console.error('Unexpected error during auth initialization:', error);
        }
      } finally {
        // Only update loading state if this is still the current effect instance
        if (effectId === initEffectIdRef.current) {
          setLoading(false);
        }
      }
    };

    initializeAuthState();
  }, [fetchUserInfo]);

  const logout = useCallback(async () => {
    try {
      await apiClient.post('/api/auth/logout', {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Clear client-side state
      setUser(null);
      // Note: httpOnly cookies are cleared server-side
    }
  }, []);

  const login = async (email, password) => {
    try {
      await apiClient.post('/api/auth/login', {
        email,
        password
      });

      // With cookie-based auth, token is automatically stored in httpOnly cookie
      // Fetch user info after successful login
      const userData = await fetchUserInfo();

      // Ensure user state is updated before returning
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed. Please check your email and password.'
      };
    }
  };

  const register = async (email, password) => {
    try {
      await apiClient.post('/api/auth/register', {
        email,
        password
      });

      // With cookie-based auth, token is automatically stored in httpOnly cookie
      // Fetch user info after successful registration
      await fetchUserInfo();

      return { success: true };
    } catch (error) {
      console.error('Registration error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed'
      };
    }
  };

  const loginWithGoogle = async (credentialResponse) => {
    try {
      // Redirect to backend Google OAuth endpoint
      window.location.href = `${API_CONFIG.BASE_URL}/api/auth/google`;
      return { success: true };
    } catch (error) {
      console.error('Google login error:', error);
      return { 
        success: false, 
        error: 'Google login failed' 
      };
    }
  };

  const refreshUser = useCallback(async () => {
    await fetchUserInfo();
  }, [fetchUserInfo]);

  const isAuthenticated = useCallback(() => {
    return !!user;
  }, [user]);

  const value = {
    user,
    login,
    register,
    loginWithGoogle,
    logout,
    refreshUser,
    isAuthenticated,
    loading
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
